import React, { useCallback, useRef, useState } from 'react'
import styled from 'styled-components'
import { asUnified, ut } from '@devnw/theme'
// eslint-import-resolver-node (the resolver wired in .eslintrc.json) does not
// understand package.json "exports" maps, so it cannot follow @devnw's
// wildcard subpath export. The module resolves correctly at build/runtime
// (webpack, Node, ts-jest) via the "exports" field — see
// __tests__/devnw-smoke.test.tsx for the Phase 0 proof.
import IamEphemeralCredentialConsole, {
  type EphemeralCredentialLeaseFilters,
  // eslint-disable-next-line import/no-unresolved
} from '@devnw/cyber-iam/iam/EphemeralCredentials/EphemeralCredentialConsole'
import type {
  CredentialLease as IamCredentialLease,
  IssueCredentialParams,
  IssueCredentialResponse,
  KeyRotationResult,
} from '@devnw/cyber-iam/iam/types'
import { useAdminGenClient } from '../../api/genClient'
import { useActiveTenant } from '../../layout/TenantSwitcher'
import {
  toIamCredentialLease,
  toIamIssueCredentialResponse,
  toIamKeyRotationResult,
} from '../../api/ephemeralCredsMapping'

function errMessage(e: unknown): string {
  return e instanceof Error ? e.message : 'Request failed'
}

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => ut(asUnified(theme)).space('lg')};
`

const ISSUER_TYPES = ['ssh', 'ephemeral']
const ISSUER_TYPES_FOR_ROTATION = ['ssh']

/**
 * OIDCPAM Gate-6 Phase 5d: migrated off MUI onto
 * @devnw/cyber-iam/iam/EphemeralCredentials/EphemeralCredentialConsole — the
 * composite parity component built from this page's three sub-pieces (lease
 * browser + table, one-time issue dialog, key-rotation cards). The console
 * now owns the tab strip, the session/subject filter inputs, the inline
 * two-phase revoke confirm, and the ONE-TIME credential-material reveal
 * (generation-token liveness guard against a close+reopen race — see the
 * component's own doc comments). This page still owns the API calls, the
 * wire-mapping (../../api/ephemeralCredsMapping — verified against the real
 * client types field-by-field; no latent snake_case/camelCase bug found in
 * this suite, unlike several sibling PAM sub-areas), and tenant/routing.
 *
 * SECURITY: `handleIssue` below RETURNS the resolved credential material to
 * the console's `onIssue` promise; it is never assigned to page state, never
 * logged, and is discarded by the component itself on dialog close. The page
 * never retains a copy.
 */
const EphemeralCredsPage = (): JSX.Element => {
  const { tenant } = useActiveTenant()
  const client = useAdminGenClient()

  const [leases, setLeases] = useState<IamCredentialLease[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | undefined>(undefined)
  // Last filters used for a search — replayed after a revoke so the lease
  // list reflects the new state without losing the operator's query.
  const lastFiltersRef = useRef<EphemeralCredentialLeaseFilters>({})

  const loadLeases = useCallback(
    async (filters: EphemeralCredentialLeaseFilters): Promise<void> => {
      lastFiltersRef.current = filters
      setLoading(true)
      setError(undefined)
      try {
        const data = await client.adminListCredentialLeases({
          tenantId: tenant,
          sessionId: filters.sessionId,
          subjectId: filters.subjectId,
        })
        setLeases((data.leases ?? []).map(toIamCredentialLease))
      } catch (e: unknown) {
        setError(errMessage(e))
      } finally {
        setLoading(false)
      }
    },
    [client, tenant]
  )

  const handleSearch = (filters: EphemeralCredentialLeaseFilters): void => {
    loadLeases(filters)
      .then(() => {})
      .catch(() => {})
  }

  const handleIssue = (
    params: IssueCredentialParams
  ): Promise<IssueCredentialResponse> =>
    client
      .adminIssueEphemeralCred({
        tenantId: tenant,
        subjectId: params.subjectId,
        // Origin production hardening: the server REQUIRES an activated-grant
        // binding for every ephemeral-credential issuance
        // (AdminIssueEphemeralCredReq.grantId is required). The @devnw
        // console's IssueCredentialConsole/IssueCredentialDialog now exposes
        // an operator-facing "Grant ID (optional)" input, so
        // IssueCredentialParams.grantId carries the operator-supplied value
        // straight through to the issue API call. If the operator leaves it
        // blank, the hardened server still rejects the unbound request
        // (fail-safe) — but grant-bound issuance is now functional end to
        // end rather than fail-closed by construction.
        grantId: params.grantId ?? '',
        sessionId: params.sessionId,
        resourceId: params.resourceId ?? '',
        issuerType: params.issuerType,
        scope: params.scope,
        ttlSeconds: params.ttlSeconds,
      })
      .then(toIamIssueCredentialResponse)

  const handleRevoke = async (leaseId: string): Promise<void> => {
    await client.adminRevokeCredentialLease({ tenantId: tenant, id: leaseId })
    await loadLeases(lastFiltersRef.current)
  }

  const handleRotate = (issuerType: string): Promise<KeyRotationResult> =>
    client
      .adminRotateCredentialKey({ tenantId: tenant, issuerType })
      .then(toIamKeyRotationResult)

  return (
    <PageContainer>
      <IamEphemeralCredentialConsole
        testID="ephemeral-creds"
        title="Ephemeral Credentials"
        leases={leases}
        onSearch={handleSearch}
        onIssue={handleIssue}
        onRevoke={handleRevoke}
        onRotate={handleRotate}
        loading={loading}
        error={error}
        issuerTypes={ISSUER_TYPES}
        rotationIssuerTypes={ISSUER_TYPES_FOR_ROTATION}
      />
    </PageContainer>
  )
}

export default EphemeralCredsPage
